// Report a loop (SPEC §6.4): choose a reason; three reports from established accounts hide it
// automatically until a stricter re-check.
import { useState } from "react";
import { ApiError, api } from "../api";
import { currentRoute, Link } from "../lib/router";
import { useSession } from "../session";
import { Modal } from "./Modal";

const REASONS: [string, string][] = [
  ["copied_melody", "It copies someone else's melody"],
  ["offensive_text", "Offensive title or nickname"],
  ["noise_abuse", "Noise or abuse, not music"],
  ["other", "Something else"],
];

export function ReportDialog({ loopId, onClose }: { loopId: string; onClose: () => void }) {
  const user = useSession((s) => s.user);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "done">("idle");
  const [error, setError] = useState("");
  return (
    <Modal title="Report this song" onClose={onClose} labelledBy="report-title">
      {!user ? (
        <p>
          Please <Link href={`/signin?next=${encodeURIComponent(currentRoute())}`}>sign in</Link> to report a loop.
        </p>
      ) : state === "done" ? (
        <div className="stack" role="status">
          <p data-testid="report-done">
            Thanks — your report is recorded. Reports from several established accounts hide a loop automatically until
            it passes a stricter check.
          </p>
          <button type="button" className="btn" onClick={onClose}>
            Close
          </button>
        </div>
      ) : (
        <form
          className="stack"
          onSubmit={async (e) => {
            e.preventDefault();
            setState("sending");
            setError("");
            try {
              await api("POST", `/api/loops/${encodeURIComponent(loopId)}/report`, { reason, note: note || undefined });
              setState("done");
            } catch (err) {
              setError(err instanceof ApiError ? err.message : "Couldn't send the report.");
              setState("idle");
            }
          }}
        >
          <fieldset className="stack">
            <legend>What's wrong?</legend>
            {REASONS.map(([v, l]) => (
              <label key={v} className="toggle">
                <input
                  type="radio"
                  name="reason"
                  value={v}
                  checked={reason === v}
                  onChange={() => setReason(v)}
                  data-testid={`reason-${v}`}
                />
                <span>{l}</span>
              </label>
            ))}
          </fieldset>
          <label className="stack-label">
            <span>Details (optional)</span>
            <textarea value={note} maxLength={500} rows={3} onChange={(e) => setNote(e.target.value)} />
          </label>
          {error && (
            <p role="alert" className="error">
              {error}
            </p>
          )}
          <div className="row">
            <button className="btn btn-primary" disabled={!reason || state === "sending"} data-testid="report-send">
              Send report
            </button>
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
